// THE EMAIL CARDS IN THE CONVERSATION (BUILD-DEC-155). The owner, on what an investor should see:
// "I want the investor to see the chat log and be able to see all the comings and goings cleanly."
//
// So email in a chat log is a THREAD OF CARDS — who wrote, what about — and any one of them opens IN PLACE.
// Two things changed from what was here before:
//   • an INBOUND reply had no card at all. It is the trigger that woke the run, so it arrives as a transcript
//     turn; the conversation rendered it as a wall of quoted email as if a person had typed it. It is a card
//     now, in the same visual family as the outbound one (`email-card` / `ec-*`, the prototype's own
//     vocabulary — no new visual language);
//   • an OUTBOUND card opened a stacked reader OVERLAY. An elevation dialog is the wrong instrument for
//     something you read mid-scroll: it covered the conversation and cost the reader their position. Every
//     message now EXPANDS where it sits and collapses again. MessageReaderBody is the one inline reader,
//     shared by inbound and outbound cards rather than duplicated behind another surface.
//
// The disclosure is a real `<button>` carrying `aria-expanded` + `aria-controls`, so it is keyboard reachable
// and a screen reader is told the truth about what the click does — the quality bar the clickable card
// already set.
//
// NOTHING IS AUTHORED HERE (P-SELF). The structured cards come from the `comms` Message projection, whose
// body still resolves server-side behind `bodyRef` (G-058); the legacy inbound fallback carries only the
// sender's words from the trigger turn. Neither is written to any event, log line, or activity payload — the
// expansion reads, it does not record.

import { useCallback, useId, useState } from 'react';
import { Icon } from './Icon.tsx';
import { EmailBodyText, MessageReaderBody } from './MessageReaderBody.tsx';
import { useSurfaceApi } from '../surfaces/SurfaceApiContext.tsx';
import { useProjection } from '../surfaces/useProjection.ts';
import type { MessageReaderProjection } from '../surfaces/know-projections.ts';
import type { InboundEmailTurn } from '../surfaces/chat-email.ts';
import type { ChatActivityItem } from '../surfaces/projections.ts';

/** Read a string field off the opaque objectRef, or undefined (a malformed ref renders nothing extra). */
function refString(row: ChatActivityItem, key: string): string | undefined {
  const value = row.objectRef?.[key];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/**
 * One card in the email thread: the collapsed header line (who + what about) and, when open, whatever body
 * the caller supplies. The panel is mounted only while open so the outbound card's projection read happens
 * on demand rather than for every email in a long conversation.
 */
function EmailDisclosure({
  direction,
  from,
  when,
  subject,
  label,
  children,
}: {
  readonly direction: 'inbound' | 'outbound';
  readonly from: string;
  /** The channel/lifecycle marker in the row's mono slot. */
  readonly when: string;
  readonly subject: string;
  readonly label: string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const toggle = useCallback(() => setOpen((current) => !current), []);
  return (
    <div className="email-thread" data-email-direction={direction}>
      <button
        type="button"
        className="email-card"
        aria-expanded={open}
        {...(open ? { 'aria-controls': panelId } : {})}
        aria-label={label}
        onClick={toggle}
      >
        <span className={direction === 'outbound' ? 'ec-ico out' : 'ec-ico'} aria-hidden="true">
          <Icon name={direction === 'outbound' ? 'send' : 'mail'} size={14} />
        </span>
        <span className="ec-main">
          <span className="ec-top">
            <span className="ec-from">{from}</span>
            <span className="ec-when">{when}</span>
          </span>
          <span className="ec-subj">{subject}</span>
        </span>
        <span className={open ? 'ec-go ec-open' : 'ec-go'} aria-hidden="true">
          <Icon name="chevron" size={14} />
        </span>
      </button>
      {open ? (
        <div id={panelId} className="email-inline">
          {children}
        </div>
      ) : null}
    </div>
  );
}

/**
 * A persisted turn's structured Message reference. Unlike the legacy prose recogniser, this reads the
 * comms-owned envelope/body/status through the actor-scoped Message endpoint and renders the same projection
 * as every other email reader. The reference is opaque: no email body is duplicated into Agent metadata.
 */
export function ReferencedEmailCard({
  messageId,
  statusSuffix = '',
}: {
  readonly messageId: string;
  /** Trace-only context which does not restate any Message-owned field (for example, refused attempts). */
  readonly statusSuffix?: string;
}): React.JSX.Element {
  const api = useSurfaceApi();
  const load = useCallback(() => api.getMessage(messageId), [api, messageId]);
  const state = useProjection<MessageReaderProjection | undefined>(load);
  const message = state.data;

  if (state.loading) {
    return <EmailReferencePlaceholder label="Loading email…" />;
  }
  if (state.error || message === null || message === undefined) {
    return <EmailReferencePlaceholder label="This email is unavailable." />;
  }

  const inbound = message.direction === 'inbound';
  // The actor-scoped Comms projection owns BOTH directions' envelope. Even the familiar outbound "Ritona"
  // label must come from that record; hardcoding it here would be a second source of truth.
  const correspondent = message.fromDisplay;
  return (
    <EmailDisclosure
      direction={message.direction}
      from={correspondent}
      when={`${messageStatusLabel(message)}${statusSuffix}`}
      subject={message.subject}
      label={
        inbound
          ? `Read the email from ${correspondent}: ${message.subject}`
          : `Read the email: ${message.subject}`
      }
    >
      <MessageReaderBody state={state} />
    </EmailDisclosure>
  );
}

/** A structured reference that cannot yet/ever be resolved remains visible without becoming a dead control. */
function EmailReferencePlaceholder({ label }: { readonly label: string }): React.JSX.Element {
  return (
    <div className="email-card" data-email-reference-state="unavailable">
      <span className="ec-ico" aria-hidden="true">
        <Icon name="mail" size={14} />
      </span>
      <span className="ec-main">
        <span className="ec-from">Email</span>
        <span className="ec-subj">{label}</span>
      </span>
    </div>
  );
}

/** Closed Message status → plain lifecycle copy. No delivery state is inferred from direction. */
function messageStatusLabel(message: MessageReaderProjection): string {
  const labels: Readonly<Record<MessageReaderProjection['status'], string>> = {
    draft: 'draft',
    sent: 'sent',
    captured: 'captured',
    received: 'received',
    routed: 'received · routed',
    discarded: 'discarded',
  };
  return labels[message.status];
}

/**
 * An OUTBOUND email trace row as a card. A successful row carries only an opaque Message id, so every
 * Message fact shown on the card is resolved from the actor-scoped Comms projection. Request/refusal/failure
 * rows have no Message and retain a plain operational fallback; they never pretend a delivery record exists.
 */
export function EmailActivityCard({
  row,
  refusedBefore = 0,
}: {
  readonly row: ChatActivityItem;
  /** Attempts collapsed into this card. Shown, never hidden — a send that took six goes is a fact about
   *  the run, and swallowing it to tidy the thread would be the tidying doing harm. */
  readonly refusedBefore?: number;
}): React.JSX.Element {
  const messageId = refString(row, 'messageId');
  // The attempts that did not land are NOT tidied out of existence — the thread shows one card, and the
  // card says what it cost. A reader who sees "sent · after 6 refused attempts" knows to go looking; a
  // reader shown only a clean "sent" has been misled by the cleanup.
  const attempts =
    refusedBefore > 0
      ? ` · after ${String(refusedBefore)} refused attempt${refusedBefore === 1 ? '' : 's'}`
      : '';
  if (messageId !== undefined) {
    return <ReferencedEmailCard messageId={messageId} statusSuffix={attempts} />;
  }

  // An ok `email_sent` row without its required Message handle is malformed, not proof that anything was
  // sent. State the missing record without synthesising a subject, envelope or delivery status.
  if (row.kind === 'email_sent' && row.outcome === 'ok') {
    return <EmailReferencePlaceholder label="The sent email record is unavailable." />;
  }

  // No Message exists for an in-flight request or a refusal/failure before delivery. This is deliberately
  // plain activity copy: its label/outcome belong to Agent's trace, while Ritona is the known internal lane.
  const when = emailLifecycleLabel(row);
  return (
    <div className="email-card" data-email-direction="outbound">
      <span className="ec-ico out" aria-hidden="true">
        <Icon name="send" size={14} />
      </span>
      <span className="ec-main">
        <span className="ec-top">
          <span className="ec-from">Ritona</span>
          <span className="ec-when">{`${when}${attempts}`}</span>
        </span>
        <span className="ec-subj">{row.label}</span>
      </span>
    </div>
  );
}

function emailLifecycleLabel(row: ChatActivityItem): string {
  if (row.outcome === 'error') return 'failed';
  if (row.outcome === 'gate_refused') return 'refused';
  if (row.kind === 'email_send_requested') return 'send requested';
  return row.outcome === 'running' ? 'sending…' : 'sent';
}

/**
 * An INBOUND email as a card. `email` is the split of the platform-authored trigger turn: the sender's
 * resolved display name and their own words. A reply that carried no readable text says so — an empty card
 * body would read as an empty email (P-SELF). There is no subject on this path by design: the sender-authored
 * subject is retained in Comms and deliberately never joins what the Agent is given, so the card states
 * what it knows (that this arrived by email) rather than inventing a heading.
 */
export function InboundEmailCard({
  email,
}: {
  readonly email: InboundEmailTurn;
}): React.JSX.Element {
  // The collapsed row shows who, the channel, and the OPENING of what they wrote. The sender-authored subject
  // is deliberately never on this legacy path (it is retained in Comms and never joins
  // what the Agent is given), and inventing a heading would be worse than showing their own first words —
  // which is what a mail client shows anyway. `.ec-subj` holds it to ONE line and ellipsises the rest, so a
  // long opening sentence cannot stretch the collapsed row — the whole line is one click away.
  const opening = email.body === null ? 'No readable text in this reply' : firstLine(email.body);
  return (
    <EmailDisclosure
      direction="inbound"
      from={email.sender}
      when="by email"
      subject={opening}
      label={`Read the email from ${email.sender}`}
    >
      {email.body === null ? (
        <div className="em-body">
          <p>This reply arrived with no readable text.</p>
        </div>
      ) : (
        <EmailBodyText body={email.body} />
      )}
      <div className="em-foot">
        <span className="hint">Received by email · on the record</span>
      </div>
    </EmailDisclosure>
  );
}

/** The sender's first non-empty line, verbatim — the collapsed row's preview. Nothing is summarised. */
function firstLine(body: string): string {
  return (
    body
      .split('\n')
      .find((line) => line.trim() !== '')
      ?.trim() ?? body.trim()
  );
}
